/**
 * Derivation of gutter label maps (message labels, tool labels) from the
 * event stream. Pure functions over Event[]; no React.
 */

import type { Event } from "@tsmono/inspect-common/types";
import { getOwn } from "@tsmono/util";

/**
 * Derive tool-event labels from message labels.
 *
 * A tool event inherits the label of the tool message it produced — matched
 * via `ToolEvent.message_id`, or via tool-role input messages on model
 * events (keyed by `tool_call_id`).
 */
export const buildToolLabels = (
  events: Event[],
  messageLabels: Record<string, string> | undefined
): Record<string, string> | undefined => {
  if (!messageLabels) return undefined;

  const toolLabels: Record<string, string> = {};
  for (const event of events) {
    if (event.event === "tool") {
      const label = event.message_id
        ? getOwn(messageLabels, event.message_id)
        : undefined;
      if (label) toolLabels[event.id] = label;
    } else if (event.event === "model") {
      for (const message of event.input) {
        if (message.role !== "tool" || !message.id) continue;
        const label = getOwn(messageLabels, message.id);
        if (label && message.tool_call_id) {
          toolLabels[message.tool_call_id] = label;
        }
      }
    }
  }

  return Object.keys(toolLabels).length > 0 ? toolLabels : undefined;
};

/**
 * Restrict the message-label map to messages actually present in `events`.
 *
 * The map is shared across the whole sample, but timelines (e.g. auditor vs
 * target) show different events — without this an unlabeled timeline would
 * reserve label-column space just because another timeline is labeled.
 */
export const scopeMessageLabels = (
  events: Event[],
  messageLabels: Record<string, string> | undefined
): Record<string, string> | undefined => {
  if (!messageLabels) return undefined;

  const present = new Set<string>();
  for (const event of events) {
    if (event.event === "model") {
      for (const message of event.input) {
        if (message.id) present.add(message.id);
      }
      for (const choice of event.output.choices) {
        if (choice.message.id) present.add(choice.message.id);
      }
    } else if (event.event === "tool" && event.message_id) {
      present.add(event.message_id);
    }
  }

  const scoped: Record<string, string> = {};
  for (const [id, label] of Object.entries(messageLabels)) {
    if (present.has(id)) scoped[id] = label;
  }
  return Object.keys(scoped).length > 0 ? scoped : undefined;
};
